// eslint-disable-next-line
import i18n from './locales/i18n';

import {cloneDeep, get, isEmpty, pick} from 'lodash';
import qs from 'qs';
import {TextDecoder, TextEncoder} from 'text-encoding';
import 'regenerator-runtime/runtime';

import {setInstanceDefaults} from 'rapid7-insight-platform-axios';

import {mapLocaleFromUnderscoreToHyphen} from 'locales/resources';
import locales from 'resources/Locales';
import 'unorm';
import {AppData, User} from 'types/store';
import ajaxRequestNotifier, {
  ACTIVE,
  IDLE,
  init as initializeAjaxRequestNotifier,
} from 'utils/ajaxRequestNotifier';
import buildAppData from 'utils/appDataBuilder';
import getRegion from 'utils/getRegion';
import {isFedRamp} from 'utils/isFedRamp';
import {getLocalData} from 'utils/localStorageUtils';
import {getLogoutPageUrl} from 'utils/logout';
import {initMetrics} from 'utils/metrics';
import {initSessionUtil} from 'utils/session';
import {setCsrf} from 'utils/session/csrf';
import setupRequestErrorFilter from 'utils/setupRequestErrorFilter';
import {injectXHRHeaders} from 'utils/xhrOverride';

import 'whatwg-fetch';
import './favicon.ico';

import l10n from './locales/l10n';
import {EA_URI} from './resources/InternalURIs';
import pendo from './utils/pendo';
import xssSync from './utils/ui-xss-sync';

// eslint-disable-next-line
declare var __webpack_public_path__: string;
// eslint-disable-next-line
__webpack_public_path__ = window?.CDN_BUCKET ?? ASSET_PATH;

type CustomAxiosOptions = {
  apiHostname: string;
  csrfToken: string;
  ipimsUserId: string;
  orgId: string;
};

const setupCustomAxiosInstance = (options: CustomAxiosOptions) => {
  const {ipimsUserId, orgId, csrfToken} = options;

  setInstanceDefaults('komand', {
    headers: {
      common: {
        CSRFToken: csrfToken,
        'X-CSRF-TOKEN': csrfToken,
        'X-IPIMS-ORG-ID': orgId,
        'X-IPIMS-USER-ID': ipimsUserId,
      },
    },
  });
};

const urlsToIgnoreWhenDeterminingAjaxIdleState = [
  {
    method: 'GET',
    url: /ea\/ra\/api\/1\/ticketing\/.*\/errors/,
  },
  {
    method: 'GET',
    url: /ea\/container\/api\/1\/registries\/failures/,
  },
];

const FIVE_MINUTES_IN_MS = 5 * 60 * 1000;

function initLocales(locale?: string) {
  locale = mapLocaleFromUnderscoreToHyphen(locale || locales.en);
  i18n.addUserLocale(locale);
  l10n.init(locale);
}

function initPendo(pendoApiKey: string, appData: AppData) {
  const {user} = appData;

  if (pendoApiKey && user && user.id && user.orgId) {
    pendo.loadPendo(pendoApiKey);
    const region = getRegion((window || {}).CDN_BUCKET);

    try {
      pendo.onPendoLoaded(
        ((user) => () => {
          window.pendo.identify(user.id, user.orgId);
          window.pendo.updateOptions({
            account: {
              edition: user.productEdition,
              name: user.orgName,
              orgId: user.orgId,
              region,
            },
            visitor: {
              ...pick(user, [
                'email',
                'firstName',
                'lastName',
                'login',
                'role',
                'isSSOEnabled',
              ]),
              isPlatformUser: appData.isFromPlatform,
              lang: user.locale || 'en_US',
              region,
              screenHeight: window.screen.height,
              screenWidth: window.screen.width,
              windowHeight: window.innerHeight,
              windowWidth: window.innerWidth,
            },
          });
        })(cloneDeep(user))
      );
    } catch (e) {
      console.error(e); // eslint-disable-line no-console
    }
  }
}

export function start(
  user: User,
  licenseOptions: string[],
  csrfToken: string,
  pendoApiKey = '',
  momentLocale: string,
  productOptions = {}
) {
  const isReportingService =
    window.location.href.indexOf('reporting-service') !== -1;

  if (isReportingService) {
    initLocales(locales.en);

    return import('./foundation/reporting').then(
      ({default: loadReportingApp}) => {
        loadReportingApp();
      }
    );
  }

  const windowLocationHref = get(window, 'location.href', '');
  const queryParams = qs.parse(
    windowLocationHref.slice(windowLocationHref.indexOf('?') + 1)
  );

  const qsParts = (get(queryParams, 'token', '') as string).split('#');
  const jsonWebToken = get(qsParts, '[0]', '');

  if (isEmpty(user) && !jsonWebToken) {
    const referrer = getLocalData('referrer', {}).url;

    if (referrer) {
      return (window.location = referrer);
    }

    return (window.location.href = PLATFORM.PANDA);
  }
  const appData = buildAppData({
    csrfToken,
    isDev,
    licenseOptions,
    productOptions,
    user,
  });

  const {
    consoleUrl,
    ipims,
    isFromConsole,
    isInternalOrg,
    user: {ipimsSessionId, ipimsUserId, consoleSessionId, orgId, locale},
  } = appData;

  if (!window.TextDecoder) {
    window.TextDecoder = TextDecoder;
  }

  if (!window.TextEncoder) {
    window.TextEncoder = TextEncoder;
  }

  setCsrf(csrfToken);
  injectXHRHeaders({
    [EA_URI.FEDRAMP_DOMAIN]: {
      iPIMSCSRFToken: ipims.csrfToken,
    },
    [EA_URI.PROD_DOMAIN]: {
      iPIMSCSRFToken: ipims.csrfToken,
    },
    [EA_URI.STAGING_DOMAIN]: {
      iPIMSCSRFToken: ipims.csrfToken,
    },
    ...(consoleUrl
      ? {
          [consoleUrl]: {
            iPIMSCSRFToken: ipims.csrfToken,
          },
        }
      : {}),
  });

  setupCustomAxiosInstance({
    apiHostname: `${location.origin}/${EA_URI.AUTOMATION_API}`,
    csrfToken,
    ipimsUserId,
    orgId,
  });

  setupRequestErrorFilter(
    getLogoutPageUrl({
      consoleUrl,
      hasConsoleSession: !!consoleSessionId,
      hasIpimsSession: !!ipimsSessionId,
      isDev,
    })
  );

  initLocales(locale);

  xssSync.init({
    consoleSessionId,
    csrfToken,
    ipimsUserId,
    jsonWebToken,
    orgId,
  });

  initializeAjaxRequestNotifier(
    urlsToIgnoreWhenDeterminingAjaxIdleState,
    FIVE_MINUTES_IN_MS
  );

  const sessionTouch = initSessionUtil(isFromConsole, consoleUrl);

  if (sessionTouch) {
    function refreshSessionOnInterval() {
      sessionTouch!.refreshSessionOnInterval({
        consoleUrl,
        user: appData.user,
      });
    }

    ajaxRequestNotifier.on(ACTIVE, refreshSessionOnInterval, true);
    ajaxRequestNotifier.on(IDLE, sessionTouch.clearRefreshSessionInterval);
  }

  if (!isInternalOrg && !isFedRamp()) {
    initPendo(pendoApiKey, appData);
  }

  initMetrics({ipimsUserId, orgId});

  return import('./foundation/app').then(({default: loadApp}) => {
    loadApp(appData);
  });
}

export default {
  start,
};
